<template>
	<R3Div class="w-full">
		<div class="flex flex-column p-inputgroup">
			<slot>
				<label v-if="props && props.label" :for="props && props.inputID" class="p-d-block">{{ props.label }}</label>

				<small v-if="props && props.helpText" :for="props && props.inputID">{{
					props.helpText
				}}</small>
				<div
					class="flex flex-row r3-input-number-wrap"
					:data-db-table="props.dbTable || undefined"
					:data-db-field="props.dbField || undefined"
					:data-db-obs="props.dbObs || undefined"
					:data-db-label="props.label || undefined"
					:data-db-controle="props.inputID || props.name || undefined"
				>
					<!--
						Senha: o PrimeVue Password não herda o @keydown do InputNumber.
						Sem onKeydown no input-props, Enter no coletor não avança o foco (login).
					-->
					<Password
						v-if="props.password"
						ref="passwordRef"
						v-model="passwordInputValue"
						class="w-full"
						:input-id="props && props.inputID"
						:placeholder="props && props.placeholder"
						:feedback="false"
						:disabled="isDisabled"
						:readonly="props.readonly"
						:input-props="passwordInputProps"
					/>

					<InputNumber v-else :id="props && props.inputID" ref="inputRef" v-model="inputNumber.inputValue"
						class="w-full" :placeholder="props && props.placeholder"
						:input-id="props && props.inputID"
						:pt="{
							...(props && props.passthrough ? props.passthrough : {}),
							// Garante id + inputmode + keydown + autocomplete no <input> real (PV 3)
							input: {
								id: props?.inputID,
								inputmode: getKeyboardInputMode(),
								autocomplete: autocompleteAttr,
								onKeydown: inputKeydownEvent,
							},
							pcInputText: {
								inputmode: getKeyboardInputMode(),
								autocomplete: autocompleteAttr,
							},
						}"
						:min-fraction-digits="props.minFractionDigits"
						:max-fraction-digits="props.maxFractionDigits"
						:min="props.min"
						:max="props.max"
						:disabled="isDisabled" :readonly="props.readonly" :use-grouping="false" @blur="onExit"
						@keydown="inputKeydownEvent"
						@keyup.enter="inputKeydownEvent"
						@input="onInputEvent" />

					<Button v-if="props.rightButtonVisible || props.dataFinderRef != null"
						:icon="`pi ${props.icon || 'pi-search'}`" :disabled="isDisabled" @click="rightBtnclick" />

					<!-- X limpa mesmo com disabledInput (campo travado após seleção, mas ainda apagável). -->
					<Button v-if="props.dataFinderRef != null" class="ml-1" icon="pi pi-times"
						:disabled="isClearDisabled" @click="onClearClick" />

					<Button v-if="props.barcodeScanVisible" icon="pi pi-camera" :disabled="isDisabled" @click="openScannerDialog" />
				</div>

				<R3BarcodeScanCamera
					v-if="props.barcodeScanVisible"
					v-model:visible="scannerDialogVisible"
					@scan-detected="onScanDetected"
				/>
			</slot>
		</div>
	</R3Div>
</template>

<script lang="ts" setup>
	import R3BarcodeScanCamera from "~/components/R3/R3BarcodeScanCamera.vue";

	/* TODO - Adicionar a possibilidade de colocar um ícone de camera
			ao lado do input para abrir a camera e ler um código de barras */
	import { useDeviceStore } from "~/store/device/deviceStore";
	import type { InputProps } from "~/types/components/R3/input.types";
	import { useExposeProperties } from "#imports";
	import { R3InputNumberClass } from "~/classes/base/R3InputBase.classes";
	import { focarProximoCampoEditavel } from "~/utils/focarProximoCampo";
	// const valor = ref<number | null>(null);

	// watch(valor, (newValue) => {
	// 	console.log("Valor alterado:", newValue);
	// 	if (newValue !== undefined) {
	// 		inputNumber.inputValue = newValue
	// 	}
	// })

	const inputNumber: R3InputNumberClass = new R3InputNumberClass();

	//const inputNumber = toRefs(new InputNumberTeste());

	const props = withDefaults(defineProps<InputProps>(), {
		useBoxShadow: false,
		buttonDataFinderVisible: false,
		barcodeScanVisible: false,
		label: "",
		mode: "decimal",
		helpText: "",
		placeholder: "",
		debounce: false,
		debounceTime: 300,
		name: "",
		password: false,
		emitEnter: false,
		disabled: false,
		disabledInput: false,
		readonly: false,
		dataFinderIgnoreFilter: false,
		/** Desliga Autofill do Android por padrão (tarja preta chave/cartão/pin). */
		autocomplete: "off",
	});

	/** Valor efetivo de autocomplete no input nativo. */
	const autocompleteAttr = computed(() => props.autocomplete || "off");

	/**
	 * Desabilitado se a prop `disabled` for true OU se a classe exposta
	 * (`inputNumber.enabled = false`) desligar o campo — padrão usado nas rotinas.
	 */
	const isDisabled = computed(
		() => Boolean(props.disabled) || Boolean(props.disabledInput) || !inputNumber.enabled,
	);

	/**
	 * Botão X: não herda `disabledInput` — só trava digitação/lupa.
	 * Com valor, continua liberado para limpar (mesmo padrão do R3Input).
	 */
	const isClearDisabled = computed(
		() => !inputNumber.inputValue || Boolean(props.disabled) || !inputNumber.enabled,
	);

	function toNumberSafe(value: unknown): number | null {
		if (value == null) return null;
		if (typeof value === "number") return Number.isFinite(value) ? value : null;
		if (typeof value === "string") {
			const normalized = value.replace(",", ".").trim();
			if (normalized === "") return null;
			const n = Number(normalized);
			return Number.isFinite(n) ? n : null;
		}
		return null;
	}

	/*
	 * Senha é texto, não número.
	 *
	 * Passar pelo `toNumberSafe` comia o zero à esquerda: quem tem senha 0812
	 * digitava quatro dígitos, o campo guardava 812 e o login recusava — medido
	 * no login do dev em 24/09/2026. Só o modo senha muda; o campo numérico
	 * comum continua igual.
	 */
	const passwordInputValue = computed<string>({
		get: () => String(inputNumber.inputValue ?? ""),
		set: (value: string) => {
			inputNumber.inputValue = value;
		},
	});

	function getKeyboardInputMode() {
		return deviceStore.desativarTeclado ? "none" : "numeric";
	}


	/**
	 * Props nativas do `<input>` do PrimeVue Password.
	 * Enter precisa estar aqui: o Password não repassa @keydown do wrapper.
	 */
	const passwordInputProps = computed(() => ({
		id: props.inputID,
		inputmode: getKeyboardInputMode(),
		autocomplete: autocompleteAttr.value,
		onKeydown: inputKeydownEvent,
		// Coletor: Enter às vezes só em keyup
		onKeyup: (e: KeyboardEvent) => {
			if (isEnterKey(e)) {
				inputKeydownEvent(e);
			}
		},
	}));

	/** Senha entra como texto; os demais campos continuam numéricos. */
	function valorParaCampo(val: number | string | null) {
		return props.password ? String(val ?? "") : toNumberSafe(val);
	}

	watch(
		() => props.modelValue,
		(val) => {
			if (val !== undefined && val !== inputNumber.inputValue) {
				inputNumber.inputValue = valorParaCampo(val);
			}
		},
		{ immediate: true }
	);

	watch(
		() => props.inputValue,
		(val) => {
			if (val !== undefined && val !== inputNumber.inputValue) {
				inputNumber.inputValue = valorParaCampo(val);
			}
		},
		{ immediate: true }
	);

	// ── DataFinder auto-sync ──────────────────────────────────────────────────
	// Quando o usuário confirma uma seleção no modal do DataFinder, o valor do
	// campo-chave (keyField) é gravado em `confirmedKeyValue`. O watcher usa
	// flush:'sync' para executar imediatamente (síncrono), garantindo que:
	//   1. O input exibe o valor-chave antes do onDialogResult do parent rodar;
	//   2. O callback do parent ainda pode sobrescrever o valor se desejar
	//      exibir outro campo (ex.: nome em vez de ID).
	// null = reset ao abrir o modal ou ao clicar em clear → não limpar o campo.
	watch(
		() => props.dataFinderRef?.confirmedKeyValue?.value,
		(keyValue) => {
			if (keyValue != null) {
				const numValue = typeof keyValue === 'number' ? keyValue : Number(keyValue);
				if (!isNaN(numValue)) {
					inputNumber.inputValue = numValue;
					emits("update:modelValue", numValue);
					emits("update:input-value", numValue);
				}
			}
		},
		{ flush: 'sync' }
	)

	// import Password from "primevue/password";
	// import { type R3InputExposedProps, type InputProps } from '';
	// import type { VNodeRef } from 'nuxt/dist/app/compat/capi';

	const emits = defineEmits([
		"update:clear",
		"update:input-value",
		"update:modelValue",
		"keydown:enter",
		"keydown",
		"blur",
		"onRightButtonClick",
	]);

	const deviceStore = useDeviceStore();

	//const inputValue = defineModel<number>("valor");

	/*watch(inputValue, (newValue) => {
		if (newValue !== undefined) {
			inputNumber.inputValue = newValue
		}
	})*/

	/*function onInputValueChange(value: number) {
		inputNumber.inputValue!.value = value;
		emits("update:modelValue", value);
	}*/

	/*function setvalor(value: T) {
		inputValue.value = value
	}*/
	function rightBtnclick() {
		if (!props.onRightButtonClick) {
			if (inputNumber.inputValue === null || inputNumber.inputValue === 0) {
				console.log("[rightBtnclick] ABRINDO MODAL (show)");
				if (props.dataFinderRef) {
					props.dataFinderRef.show();
				}
			} else {
				(async () => {
					const dataFinder = props.dataFinderRef; // Atribui a uma variável local
					if (dataFinder) {
						// Verifica a variável local
						// Nota: `keyField` fica na instância exposta do DataFinder (via autoRef ou
						// ref de componente). Usa `dataFinder.keyField` em vez de
						// `dataFinder.value.keyField` para cobrir tanto o autoRef quanto o objeto
						// exposto diretamente.
						dataFinder
							.search({
								sync: true,
								searchField: dataFinder.keyField,
								searchValue: inputNumber.inputValue!,
								ignoreFilter: props.dataFinderIgnoreFilter,
							})
							.then((result) => {
								if (result.length > 0) {
									if (dataFinder.onDialogResult) {
										dataFinder.onDialogResult(result[0]);
									}
								}
							});
					}
				})();
			}
		} else {
			props.onRightButtonClick();
		}
	}

	function onClearClick() {
		// Limpa o valor interno e emite eventos para atualizar v-models e callbacks
		inputNumber.inputValue = null;

		// Reseta o confirmedKeyValue para que a próxima seleção da mesma linha
		// no DataFinder continue disparando o watcher (null → valor = change).
		if (props.dataFinderRef) {
			props.dataFinderRef.confirmedKeyValue.value = null;
		}

		emits('update:clear');
		emits('update:input-value', null);
		emits('update:modelValue', null);
	}

	//const inputMode = ref(props.mode);

	// template refs
	const inputRef = ref<HTMLInputElement | null>(null);
	/** Ref do PrimeVue Password (modo senha) — InputNumber não monta. */
	const passwordRef = ref<{ $el?: HTMLElement } | null>(null);


	//const inputFocus = useFocus(inputRef);

	//const focusIconeDataFinder = useFocus()

	// const datafinderRef = ref<R3DataFinderExposedProps>(null);

	//const iconDataFinderRef = useTemplateRef("iconDataFinderRef");

	/*const focusInput = () => {
		inputRef.value?.$el.querySelector('input').focus();
	}
		
	function focarIconeDataFinder() {
		iconDataFinderRef.value?.$el.focus();
	}*/

	function onExit(e: FocusEvent) {
		/*if (inputNumber.inputValue) {
			props.dataFinderRef.search({
				searchValue: inputNumber.inputValue
			})
		}*/

		// Verifica se o blur está acontecendo porque o usuário está interagindo com DataFinder
		// Se relatedTarget é um botão próximo ao input ou é do DataFinder, não sobrescreve
		const relatedTarget = e.relatedTarget as HTMLElement | null;
		const target = e.target as HTMLInputElement | null;

		if (!target) {
			emits("blur", inputNumber.inputValue);
			return;
		}

		if (relatedTarget) {
			// Verifica se é o botão de search (ícone lupa) ou clear do próprio input group
			const isDataFinderButton = relatedTarget.closest('.p-inputgroup') !== null;

			// Verifica se é algum elemento do DataFinder dialog
			const isDataFinderElement = relatedTarget.closest('.r3-data-finder') !== null ||
				relatedTarget.classList.contains('p-datatable-tbody') ||
				relatedTarget.closest('.p-dialog') !== null;

			// Se está focando em um botão do input group ou no DataFinder, não atualiza o valor
			// pois provavelmente está prestes a selecionar um novo valor
			if (isDataFinderButton || isDataFinderElement) {
				emits("blur", inputNumber.inputValue);
				return;
			}
		}

		inputNumber.inputValue = toNumberSafe(target.value);
		emits("blur", inputNumber.inputValue);
	}

	// function onValueChange(value: number) {
	// 		// if (inputNumber.inputValue !== null) {
	// 		// 	inputNumber.inputValue += value;
	// 		// } else {
	// 		// 	inputNumber.inputValue = value;
	// 		// }
	// 		inputNumber.inputValue! = value
	// 		emits("update:modelValue", inputNumber.inputValue);
	// }

	onMounted(() => {
		// Android Autofill: reforça autocomplete no <input> real do PrimeVue.
		const nativeInput = inputRef.value?.$el?.querySelector?.("input") as HTMLInputElement | null | undefined;
		if (nativeInput) {
			nativeInput.setAttribute("autocomplete", autocompleteAttr.value);
			nativeInput.setAttribute("autocorrect", "off");
			nativeInput.setAttribute("autocapitalize", "off");
			nativeInput.setAttribute("spellcheck", "false");
		}

		watch(
			() => deviceStore.desativarTeclado,
			(newValue) => {
				if (newValue === true) {
					// @ts-ignore
					inputRef.value?.$el.querySelector('input').setAttribute('inputmode', 'none');
				} else {
					// @ts-ignore
					inputRef.value?.$el.querySelector('input').setAttribute('inputmode', getKeyboardInputMode());
				}
			},
			{ immediate: true }
		);

		// Emitir evento em tempo real a cada alteração, mantendo sincronizado com os modelos
		watch(
			() => inputNumber.inputValue,
			(newVal) => {
				if (newVal !== props.modelValue && newVal !== props.inputValue) {
					emits("update:modelValue", newVal);
					emits("update:input-value", newVal);
				}
			}
		);

		inputNumber.name = props.name;
		// if (inputNumber.name === "InputUMA") {
		// 	debugger;
		// }

		inputNumber.elemento.value = inputRef.value?.$el.querySelector('input');
	});

	/*watch(() => deviceStore.desativarTeclado, (newValue) => {
		if (newValue === true)
			inputMode.value = "none";
		
		else
			inputMode.value = props.mode;
	});*/

	// watch(() => deviceStore.desativarTeclado, (value: boolean) => {
	//   if (value === true) {
	//     inputMode.value = 'none';
	//   }
	//   else {
	//     inputMode.value = props.mode;
	//   }
	// })

	// watch(() => deviceStore.desativarTeclado, () => {
	//   props.mode = deviceStore.desativarTeclado === true ? 'none' : 'text';
	// })

	// if (props.debounce === true) {
	// 	watch(debouncedInputValue, (value) => {
	// 		emits("update:modelValue", value);
	// 	});
	// }
	// else {
	// 	watch(inputValue, (value) => {
	// 		emits("update:modelValue", value);
	// 	});
	// }

	// function abrirTecladoNumericoParaSenha() {
	//   password.value?.type = 'numeric';
	// }

	/**
	 * Avança o foco para o próximo campo (Enter = Tab do Delphi).
	 * Implementação central em `utils/focarProximoCampo.ts`.
	 */
	function simularTeclaTab() {
		const origemPassword = passwordRef.value?.$el as HTMLElement | undefined;
		const origemInput = inputRef.value?.$el as HTMLElement | undefined;
		focarProximoCampoEditavel(
			(document.activeElement as HTMLElement | null)
			?? origemPassword
			?? origemInput
			?? null,
		);
	}

	const { showFieldDbInfo, nomeFormularioAtual } = useFieldDbInfo();

	/**
	 * Coletor/Android/leitor: Enter às vezes vem só como keyCode/which 13
	 * (key = "Unidentified"), e o modifier Vue `.enter` não pega.
	 */
	function isEnterKey(e: KeyboardEvent): boolean {
		return (
			e.key === "Enter"
			|| e.key === "NumpadEnter"
			|| e.code === "Enter"
			|| e.code === "NumpadEnter"
			|| (e as KeyboardEvent & { keyCode?: number }).keyCode === 13
			|| (e as KeyboardEvent & { which?: number }).which === 13
		);
	}

	/**
	 * Coletor: o Enter chega em keydown e às vezes de novo em keyup.enter.
	 * Sem debounce, handlers async (ex.: mercadoria B311 EAN→ID) rodam 2x:
	 * 1º com EAN (ok) e 2º com o ID já resolvido (trata como código interno).
	 */
	let lastEnterEmitAt = 0;
	const ENTER_EMIT_MS = 400;

	function shouldEmitEnter(): boolean {
		const agora = Date.now();
		if (agora - lastEnterEmitAt < ENTER_EMIT_MS) {
			return false;
		}
		lastEnterEmitAt = agora;
		return true;
	}

	/**
	 * Traz para o v-model o que já está no `<input>`.
	 *
	 * Quando o texto vem do IME — o caso do leitor do coletor, que entrega o
	 * código inteiro num `insertText` — o PrimeVue InputNumber só sincroniza o
	 * `v-model` no blur. Sem isto, o Enter que chega logo depois do bipe emite
	 * `keydown:enter` com valor vazio, e a tela recebe nada mesmo tendo o código
	 * na frente do operador. Medido no Urovo CT-48 em 14/08/2026.
	 *
	 * Lê de `e.target` para valer tanto no InputNumber quanto no Password.
	 */
	function sincronizarComDom(e: KeyboardEvent) {
		const el = e.target as HTMLInputElement | null;
		if (!el || typeof el.value !== "string") {
			return;
		}

		// Senha vai como texto — converter aqui devolveria o problema do zero.
		if (props.password) {
			if (el.value !== inputNumber.inputValue) {
				inputNumber.inputValue = el.value;
			}

			return;
		}

		const doDom = toNumberSafe(el.value);
		if (doDom !== null && doDom !== inputNumber.inputValue) {
			inputNumber.inputValue = doDom;
		}
	}

	function inputKeydownEvent(e: KeyboardEvent) {
		// Não pegar targetValue do keydown pois ele tem o valor antigo,
		// o evento @input lidará com a atualização real-time.

		// F10 — origem no banco (LIST_F1), se mapeado
		if (e.key === "F10") {
			if (props.dbTable || props.dbField) {
				e.preventDefault();
				e.stopPropagation();
				showFieldDbInfo({
					tabela: props.dbTable || "—",
					campo: props.dbField || "—",
					formulario: nomeFormularioAtual(),
					controle: props.inputID || props.name || "R3InputNumber",
					label: props.label,
					observacao: props.dbObs,
					valorAtual: inputNumber.inputValue,
				});
			}
			return;
		}

		if (isEnterKey(e)) {
			e.preventDefault();
			if (!shouldEmitEnter()) {
				return;
			}
			sincronizarComDom(e);
			if (props.debounce === true) {
				setTimeout(() => {
					emits("keydown:enter", inputNumber.inputValue);
					// emitEnter: a tela controla o próximo campo (evita corrida async).
					if (!props.emitEnter) {
						simularTeclaTab();
					}
				}, props.debounceTime);
			} else {
				if (props.dataFinderRef && inputNumber.inputValue !== null && !props.emitEnter) {
					rightBtnclick();
				} else {
					emits("keydown:enter", inputNumber.inputValue!);
					// Também repassa o KeyboardEvent p/ handlers legados (`@keydown` com keyCode 13)
					emits("keydown", e);
					// Com emitEnter o @keydown:enter da tela faz o setFocus (Qtd Caixa → Emb, etc.)
					if (!props.emitEnter) {
						simularTeclaTab();
					}
				}
			}
			return;
		}

		// Demais teclas: repassa para a tela
		emits("keydown", e);
	}

	function onInputEvent(event: any) {
		if (event && event.value !== undefined) {
			inputNumber.inputValue = event.value;
			// O watcher observando inputNumber.inputValue se encarregará de emitir o "update:modelValue"
		}
	}

	// Visibilidade de dialogs

	const dataFinderDialogVisible = ref(false);

	function openDataFinderDialog() {
		if (inputNumber.inputValue === null) {
			//) {
			//dataFinderDialogVisible.value = true;
			if (props.dataFinderRef) {
				//console.log("dataFinderDialogVisible");
				props.dataFinderRef.show();
			}
		}
	}

	const scannerDialogVisible = ref(false);
	function openScannerDialog() {
		scannerDialogVisible.value = true;
	}

	function closeScannerDialog() {
		scannerDialogVisible.value = false;
	}

	/**
	 * Código lido pela câmera.
	 *
	 * Este input é numérico, e leitor de código de barras devolve texto: o valor
	 * passa por `toNumberSafe` antes de entrar no modelo. Código com letra — um
	 * CODE 39 alfanumérico, por exemplo — não é aceito aqui, e o campo fica como
	 * estava em vez de virar `NaN`.
	 */
	function onScanDetected(codigo: string) {
		const numero = toNumberSafe(codigo);

		if (numero === null) {
			return;
		}

		inputNumber.inputValue = numero;

		emits("update:modelValue", numero);
		emits("update:input-value", numero);
		emits("keydown:enter", numero);
	}

	// const { visible } = toRefs(inputNumber);

	/**
	 * Foca o input interno do PrimeVue InputNumber (ou Password).
	 * Mesmo padrão do R3Input — usado no login e no retorno de foco do coletor.
	 */
	function setFocus(params?: { limparCampo?: boolean }) {
		if (params?.limparCampo) {
			inputNumber.inputValue = null;
		}

		// Modo senha: o root do Password envolve o <input type="password">
		const passwordEl = passwordRef.value?.$el as HTMLElement | undefined;
		if (passwordEl) {
			const input = passwordEl.tagName === "INPUT"
				? passwordEl
				: passwordEl.querySelector("input");
			input?.focus();
			return;
		}

		const el = inputRef.value?.$el as HTMLElement | undefined;
		if (el) {
			const input = el.tagName === "INPUT" ? el : el.querySelector("input");
			input?.focus();
			return;
		}

		// Fallback por id (login: inputSenha / inputUsuario)
		if (props.inputID) {
			const byId = document.getElementById(props.inputID) as HTMLInputElement | null;
			if (byId) {
				byId.focus();
				return;
			}
		}

		// Fallback: elemento gravado no onMounted (classe de estado)
		const fallback = inputNumber.elemento?.value as HTMLElement | null | undefined;
		fallback?.focus?.();
	}
	inputNumber.setFocus = setFocus;

	type ExposeType = {
		setFocus: typeof setFocus;
	} & typeof inputNumber;

	const expose = {
		setFocus,
	} as ExposeType;

	//defineExpose(expose);
	useExposeProperties(expose, inputNumber);

	defineExpose(expose);
</script>

<style lang="scss" scoped>
	[v-cloak] {
		display: none;
	}

	.p-overlay-open {
		box-shadow: none !important;
	}

	*:focus {
		box-shadow: none !important;
	}
</style>
